export type PageName =
  | 'Home'
  | 'Dashboard'
  | 'Projects'
  | 'Planning'
  | 'Validation'
  | 'Reports'
  | 'SubsidiaryManagement'
  | 'UserManagement'
  | 'Settings'
  | 'Profile'
  | 'ProjectDetail';

const ROUTE_MAP = {
  Home: '/',
  Dashboard: '/',
  Projects: '/projects',
  Planning: '/planning',
  Validation: '/validation',
  Reports: '/reports',
  SubsidiaryManagement: '/admin/subsidiaries',
  UserManagement: '/admin/users',
  Settings: '/settings',
  Profile: '/account/profile',
  ProjectDetail: '/projects/:id'
} as const satisfies Record<PageName, string>;

type BuildOptions = {
  params?: Record<string, string | number>;
  query?: Record<string, string | number | boolean | undefined | null>;
};

/**
 * Create an app URL from a logical page name, with optional params and query.
 * If the page name is unknown, returns a lowercase path like `/${name}`.
 */
export function createPageUrl(
  page: PageName | string,
  options: BuildOptions = {}
): string {
  const base = ROUTE_MAP[page as PageName] ?? normalizeFallback(page);
  const withParams = applyParams(base, options.params);
  const withQuery = appendQuery(withParams, options.query);
  return withQuery;
}

function normalizeFallback(name: string): string {
  if (!name) return '/';
  return name.startsWith('/') ? name : `/${name.toLowerCase()}`;
}

function applyParams(path: string, params?: Record<string, string | number>) {
  if (!params) return path;
  let out = path;
  for (const [key, value] of Object.entries(params)) {
    const val = String(value);
    // Replace :key or [key]
    out = out.replace(new RegExp(`:${key}(?=/|$)`), val);
    out = out.replace(new RegExp(`\\[${key}\\]`), val);
  }
  return out;
}

function appendQuery(path: string, query?: Record<string, string | number | boolean | undefined | null>) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
